import { AppType } from "client/Api/Apps/common";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	Checkbox,
	FormItem,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { generateTrackingId, hasFlag } from "client/Misc/utils";
import {
	IInputAttachable
} from "client/Forms/attachable";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import {
	EditSharePointAppViewModel,
	SaveSharePointAppModel,
	SharePointAction,
	saveSharePointApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditSharePointAppProps = {
	initialSettings: EditAppSettings<EditSharePointAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditSharePointApp = forwardRef(
	(
		props: EditSharePointAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveSharePointAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveSharePointAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveSharePointAppModel>(
			formHandler,
			saveSharePointApp,
			AppType.SharePoint
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const enabledActionsHandler: IInputAttachable<SharePointAction> = formHandler
			.for("enabledActions");

		const addActionHandler = useCallback(
			(action: SharePointAction): void => {
				setFormData(model => ({
					...model,
					enabledActions: model.enabledActions | action
				}));
			},
			[]
		);

		const removeActionHandler = useCallback(
			(action: SharePointAction): void => {
				setFormData(model => ({
					...model,
					enabledActions: model.enabledActions & (~action)
				}));
			},
			[]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("destinationWindow", "enabledActions")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				<FormItem label={localize("_EnableActions")}>
					<NoUniqueInputIdContext>
						<ActionCheckbox
							actionKind={SharePointAction.OpenTeamSiteInSharePoint}
							addAction={addActionHandler}
							removeAction={removeActionHandler}
							enabledActions={enabledActionsHandler
								.reducer
								.value
							}
							trackingId={enabledActionsHandler
								.reducer
								.path
							}
						/>
						<ActionCheckbox
							actionKind={SharePointAction.OpenInDocumentsApp}
							addAction={addActionHandler}
							removeAction={removeActionHandler}
							enabledActions={enabledActionsHandler
								.reducer
								.value
							}
							trackingId={enabledActionsHandler
								.reducer
								.path
							}
						/>
					</NoUniqueInputIdContext>
					<ValidationError
						{...enabledActionsHandler
							.attachToValidationError()}
					/>
				</FormItem>
			</EditAppBase>
		);
	}
);
EditSharePointApp.displayName = "EditSharePointApp";

const validation = createValidationConfiguration<SaveSharePointAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("enabledActions")
	.add(
		inline((value: SharePointAction): InputValidationResult => {
			if (value === SharePointAction.None) {
				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_PleaseSelectAtLeastOneItem")
				};
			}

			return {
				kind: InputValidationResultKind.Valid
			};
		})
	);

type ActionCheckboxProps = {
	enabledActions: SharePointAction;
	actionKind: Exclude<SharePointAction, SharePointAction.None>;
	addAction(action: SharePointAction): void;
	removeAction(action: SharePointAction): void;
	trackingId: string;
};

const ActionCheckbox = (props: ActionCheckboxProps): ReactElement => {
	const {
		actionKind,
		enabledActions,
		addAction,
		removeAction,
		trackingId
	} = props;

	const isChecked = useMemo(
		(): boolean => hasFlag(enabledActions, actionKind),
		[
			actionKind,
			enabledActions
		]
	);

	const changeHandler = useCallback(
		(newValue: boolean): void => {
			if (newValue) {
				addAction(actionKind);
			} else {
				removeAction(actionKind);
			}
		},
		[
			actionKind,
			addAction,
			removeAction
		]
	);

	return (
		<Checkbox
			onChange={changeHandler}
			value={isChecked}
			text={actionKindToDisplayName[actionKind]}
			trackingId={generateTrackingId(trackingId, `|${actionKind}`)}
		/>
	);
};
ActionCheckbox.displayName = "ActionCheckbox";

const actionKindToDisplayName: { [key in Exclude<SharePointAction, SharePointAction.None>]: string; } = {
	[SharePointAction.OpenInDocumentsApp]: localize("_OpenDocuments"),
	[SharePointAction.OpenTeamSiteInSharePoint]: localize("_OpenSiteInSharePoint"),
};

function convertToSaveModel(
	initialSettings: EditAppSettings<EditSharePointAppViewModel>
): SaveSharePointAppModel {
	const {
		destinationWindow,
		enabledActions
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		enabledActions
	};
}